import dayjs from "dayjs";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import { formatReportTypeColumnLabel } from "../../../utils/reports/reportDisplayLabels";
import {
  pickHoldingBenchmarkFields,
  withTxnBenchmarkFields,
} from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  extractStockTaxRows,
  extractStockTaxSummaryTotal,
  parseStockTaxReport,
  STOCK_TAX_INTRADAY_REPORT,
  STOCK_TAX_LONG_TERM_REPORT,
  STOCK_TAX_SHORT_TERM_REPORT,
} from "../../../utils/equity/stockTaxReport";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";
import { REIT_POINT_TO_POINT_REPORT, REIT_TAX_REPORT } from "./reitReportConfig";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const resolveCurrentDateOrToday = (value: unknown): string =>
  formatEpochDate((value as Record<string, unknown>)?.currentDate ?? value ?? Date.now());

const getHoldingSchemeName = (row: Record<string, any>): string =>
  String(row.symbol ?? row.schemeName ?? row.name ?? "-");

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  const list =
    holding.transactions ?? holding.transaction ?? holding.txns ?? holding.trxn ?? [];
  return Array.isArray(list) ? list : [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  ).trim();
  if (!raw) return false;
  return isEquitySellType(raw) || raw.toLowerCase().includes("sell");
};

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "" || v === "-") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

const filterPositiveUnits = (rows: any[]) =>
  rows.filter((row) => {
    if (!row || typeof row !== "object") return true;
    const units = toNum(row.units ?? row.quantity ?? row.qty);
    if (units === null) return true;
    if (units > 0) return true;
    const invested = toNum(row.investedAmount ?? row.costValue ?? row.amount);
    const current = toNum(row.currentAmount ?? row.currentValue ?? row.marketValue);
    return (invested !== null && invested > 0) || (current !== null && current > 0);
  });

const filterRealizedTxnRows = (rows: any[]) =>
  rows.filter((r) => isSellTxn(r)).map((r) => ({
    ...r,
    type: formatReportTypeColumnLabel("Realized", "Realized"),
    displayTransType: "Realized",
  }));

const getHoldingIsin = (
  h: Record<string, any>,
  hMeta?: Record<string, any>,
): string => {
  const meta = hMeta ?? h?.metadata ?? h?.meta ?? h?.metaData ?? {};
  const val =
    h?.isin ??
    h?.ISIN ??
    h?.schemeCode ??
    meta.isin ??
    meta.ISIN ??
    meta.schemeCode;
  if (val == null || val === "") return "-";
  return String(val);
};

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val = row.averageDays ?? row.averageDay ?? row.days ?? meta.averageDays ?? meta.days;
  return val != null && val !== "" ? val : "-";
};

/** REIT uses `cagrReit` as the specific CAGR key (parallel to `cagrIntlEquity` for IntlEquity). */
const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrReit"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const normalizeUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      symbol: row.symbol ?? schemeName,
      isin: getHoldingIsin(row, meta),
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      units: row.units ?? row.quantity,
      amount: row.amount ?? row.investedAmount ?? row.costValue,
      currentAmount: row.currentAmount ?? row.currentValue ?? row.marketValue,
      averagePrice:
        row.averagePrice ?? row.avgPrice ?? row.averageNav ?? meta.averagePrice,
      currentPrice:
        row.currentPrice ?? row.currentNAV ?? row.currentNav ?? meta.currentPrice,
      gainLoss:
        row.gainLoss ?? row.unrealisedPNL ?? row.unrealizedPNL ?? row.unrealisedGainLoss,
      gainLossPerc:
        row.gainLossPerc ??
        row.gainLossPercentage ??
        row.unrealisedPNLPercentage ??
        row.unrealizedPNLPercentage,
      days: resolveHoldingAverageDays(row),
      cagr: resolveHoldingCagr(row),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

const normalizeRealizedRow = (row: Record<string, any>) => {
  const meta = row.metadata ?? row.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  const avgBuy =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.avgPrice ??
    row.averagePrice ??
    meta.avgBuyPrice ??
    meta.averagePrice;
  const sellPrice =
    row.currentPrice ?? row.currentNAV ?? row.purchaseNAV ?? row.purchasePrice;
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      symbol: row.symbol ?? schemeName,
      isin: getHoldingIsin(row, meta),
      type: formatReportTypeColumnLabel("Realized", "Realized"),
      transactionDate: formatEpochDate(
        row.transactionDate ?? row.transactiondate ?? row.date,
      ),
      purchaseNAV: sellPrice ?? row.purchaseNAV ?? "-",
      avgBuyPrice: avgBuy ?? "-",
      saleValue: row.saleValue ?? row.amount ?? row.currentAmount,
      realisedPnl:
        row.realisedPnl ??
        row.realisedPnL ??
        row.realisedPNL ??
        row.realizedPnl ??
        row.gainLoss,
      realisedPNLPercentage:
        row.realisedPNLPercentage ??
        row.realisedPnLPercentage ??
        row.realizedPNLPercentage ??
        row.gainLossPerc ??
        row.gainLossPercentage,
      cagr: resolveHoldingCagr(row),
    }),
  );
};

const flattenReitHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const schemeName = getHoldingSchemeName(h);
    const list = getHoldingTransactionList(h);
    if (!list.length) continue;
    const holdingBenchmarks = pickHoldingBenchmarkFields(h);
    for (const t of list) {
      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...holdingBenchmarks,
            ...t,
            schemeName: t.schemeName ?? schemeName,
            symbol: t.symbol ?? h.symbol ?? schemeName,
            isin:
              getHoldingIsin(t, t.metadata ?? t.meta) !== "-"
                ? getHoldingIsin(t, t.metadata ?? t.meta)
                : getHoldingIsin(h),
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transactionDate: formatEpochDate(
              t.transactionDate ?? t.transactiondate ?? t.date,
            ),
            currentDate: resolveCurrentDateOrToday(t.currentDate ?? h.currentDate),
            transType: t.transType ?? t.txnType ?? t.transactionType,
            purchasePrice:
              t.purchasePrice ?? t.price ?? t.avgPrice ?? t.averagePrice,
            currentPrice: t.currentPrice ?? t.currentNAV ?? h.currentPrice,
            amount: t.amount ?? t.costValue ?? t.investedAmount,
            currentAmount: t.currentAmount ?? t.currentValue ?? h.currentAmount,
            units: t.units ?? t.quantity,
            days: t.days ?? t.avgDays ?? h.averageDays,
            cagr: t.cagr ?? h.cagr ?? resolveHoldingCagr(h),
          }),
        ),
      );
    }
  }
  return txns;
};

const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const normalized = normalizeUnrealizedHoldingRow({ ...row, type: "Unrealized" });
  return {
    ...normalized,
    costValue: normalized.amount ?? normalized.costValue ?? normalized.investedAmount,
    currentValue: normalized.currentAmount ?? normalized.currentValue,
    gainLoss: normalized.gainLoss,
    gainLossPercentage: normalized.gainLossPerc ?? normalized.gainLossPercentage,
  };
};

const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const schemeName = getHoldingSchemeName(row);
  return {
    ...normalizeRealizedRow({ ...row, schemeName }),
    schemeName,
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    costValue: isSell ? "-" : (row.amount ?? row.costValue),
    currentValue: row.currentAmount ?? row.currentValue,
  };
};

const extractReitHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null => {
  const info =
    payload?.totalHoldingInfo ??
    payload?.totalHolding ??
    payload?.data?.totalHoldingInfo ??
    null;
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return {
    ...info,
    totalValueInvested:
      info.totalValueInvested ?? info.totalInvested ?? info.investedAmount,
    totalInvested:
      info.totalInvested ?? info.totalValueInvested ?? info.investedAmount,
    currentValue: info.currentValue ?? info.currentAmount,
    unrealisedGainLoss: info.unrealisedGainLoss ?? info.gainLoss,
    gainLoss: info.gainLoss ?? info.unrealisedGainLoss,
    gainLossPercentage: info.gainLossPercentage ?? info.gainLossPerc ?? info.roi,
    cagrReit: info.cagrReit ?? info.cagr,
    cagr: info.cagr ?? info.cagrReit,
  };
};

const normalizeMisMarketValueToCurrentValue = (rows: any[]): any[] =>
  rows.map((row) => {
    if (!row || typeof row !== "object") return row;
    const r = row as Record<string, any>;
    const currentValue =
      r.currentValue ?? r.currentAmount ?? r.marketValue ?? r.market_value;
    return {
      ...r,
      currentValue,
      dateOfInvestment: formatEpochDate(r.dateOfInvestment ?? r.trxnDate),
    };
  });

const isP2pSellRow = (row: Record<string, any>): boolean => {
  if (
    row.sellingAmount != null ||
    row.sellingNav != null ||
    row.sellingNAV != null
  ) {
    return true;
  }
  const rawLabel = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  ).trim();
  if (isEquitySellType(rawLabel)) return true;
  const raw = rawLabel.replace(/\s+/g, "_").toUpperCase();
  return raw.includes("SELL") || raw.includes("REDEEM") || raw === "REDEMPTION";
};

const mapReitP2pReportRow = (row: Record<string, any>): Record<string, any> => {
  const isSell = isP2pSellRow(row);
  const unrealPnlRaw =
    row.unrealisedPNL ?? row.unrealisedPnL ?? row.unrealizedPNL;
  const unrealPercRaw =
    row.unrealisedPNLPercentage ??
    row.unrealisedPnLPercentage ??
    row.unrealizedPNLPercentage;
  const realisedPnlRaw =
    row.realisedPnl ?? row.realisedPNL ?? row.realizedPnl ?? row.realizedPNL;
  const realisedPercRaw =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage;
  return withTxnBenchmarkFields({
    ...row,
    symbol: row.symbol ?? row.schemeName ?? "-",
    exchange: row.exchange ?? "-",
    transType: isSell ? "Sell" : "Buy",
    units: row.units ?? row.quantity,
    investedAmount: isSell
      ? (row.sellingAmount ?? row.amount)
      : (row.investedAmount ?? row.amount),
    currentAmount: isSell ? "-" : (row.currentAmount ?? row.currentValue),
    avgPrice: row.avgPrice ?? row.purchaseNAV ?? row.purchaseNav,
    currentNAV: isSell
      ? (row.sellingNav ?? row.sellingNAV ?? row.currentNAV ?? row.currentNav)
      : (row.currentNAV ?? row.currentNav),
    xirr: row.xirr ?? "-",
    cagr: row.cagr ?? "-",
    unrealisedPNL: isSell ? "-" : (unrealPnlRaw ?? "-"),
    unrealisedPNLPercentage: isSell ? "-" : (unrealPercRaw ?? "-"),
    realisedPnl: isSell ? (realisedPnlRaw ?? "-") : "-",
    realisedPNL: isSell ? (realisedPnlRaw ?? "-") : "-",
    realisedPNLPercentage: isSell ? (realisedPercRaw ?? "-") : "-",
  });
};

const extractReitP2pRows = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.response)) return payload.response;
  return [];
};

const extractP2pTotalHoldingInfo = (payload: any): Record<string, unknown> | null => {
  const cagr =
    payload?.cagr ?? payload?.data?.cagr ?? payload?.data?.data?.cagr ?? null;
  if (cagr === null || cagr === undefined) return null;
  return { cagr };
};

const extractTaxReportPayload = (payload: any) =>
  payload?.response ?? payload?.data?.response ?? payload?.data ?? payload;

const mapReitTaxTypeLabel = (type: unknown, fallback: string): string => {
  const t = String(type ?? fallback ?? "").toLowerCase();
  if (t.includes("intra")) return STOCK_TAX_INTRADAY_REPORT;
  if (t.includes("short")) return STOCK_TAX_SHORT_TERM_REPORT;
  if (t.includes("long")) return STOCK_TAX_LONG_TERM_REPORT;
  return String(type ?? fallback ?? "-");
};

const flattenReitTaxRows = (
  parsed: ReturnType<typeof parseStockTaxReport>,
): { rows: Record<string, unknown>[]; taxTotalGainLoss: number | null } => {
  if (!parsed) return { rows: [], taxTotalGainLoss: null };
  const buckets = [
    STOCK_TAX_INTRADAY_REPORT,
    STOCK_TAX_SHORT_TERM_REPORT,
    STOCK_TAX_LONG_TERM_REPORT,
  ];
  const rows = buckets.flatMap((rt) =>
    extractStockTaxRows(parsed, rt).map((r) => ({
      ...r,
      Type: mapReitTaxTypeLabel(r.Type, rt),
    })),
  );
  let taxTotal = 0;
  let hasSummary = false;
  for (const rt of buckets) {
    const total = extractStockTaxSummaryTotal(parsed, rt);
    if (total != null) {
      taxTotal += total;
      hasSummary = true;
    }
  }
  return { rows, taxTotalGainLoss: hasSummary ? taxTotal : null };
};

export type ParsedReitReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
  taxTotalGainLoss?: number | null;
};

export const parseReitReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedReitReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;
  let taxTotalGainLoss: number | null = null;

  switch (reportType) {
    case "Holding": {
      const holdings = extractReitHoldings(payload);
      rows = filterPositiveUnits(holdings).map(normalizeUnrealizedHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) ?? rows.length;
      break;
    }
    case "Realized": {
      const holdings = extractReitHoldings(payload);
      rows = filterRealizedTxnRows(
        flattenReitHoldingsTransactions(holdings),
      ).map(normalizeRealizedRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "All": {
      const holdings = extractReitHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      const unrealized = filterPositiveUnits(holdings).map(normalizeHoldingRowForAll);
      const realized = filterRealizedTxnRows(
        flattenReitHoldingsTransactions(holdings),
      ).map(normalizeTxnRowForAll);
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction": {
      const holdings = extractReitHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      rows = flattenReitHoldingsTransactions(holdings);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "MIS Report": {
      const raw = Array.isArray(payload) ? payload : payload?.data ?? payload ?? [];
      rows = normalizeMisMarketValueToCurrentValue(
        Array.isArray(raw) ? raw : [],
      ).sort(
        (a, b) => Number(b.absoluteReturnPerc ?? 0) - Number(a.absoluteReturnPerc ?? 0),
      );
      break;
    }
    case REIT_POINT_TO_POINT_REPORT: {
      rows = extractReitP2pRows(payload).map(mapReitP2pReportRow);
      totalHoldingInfo = extractP2pTotalHoldingInfo(payload);
      break;
    }
    case REIT_TAX_REPORT: {
      const parsed = parseStockTaxReport(extractTaxReportPayload(payload));
      const flattened = flattenReitTaxRows(parsed);
      rows = flattened.rows;
      taxTotalGainLoss = flattened.taxTotalGainLoss;
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
    taxTotalGainLoss,
  };
};

export const mergeReitTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.symbol ?? r.schemeName}-${r.transactionDate}-${r.units}-${r.transType}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.symbol ?? row.schemeName}-${row.transactionDate}-${row.units}-${row.transType}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
